import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Select, Row, Radio, Col, message, RadioChangeEvent } from 'antd'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form'
import { StatusTag } from '@ProdWb/components/StatusTag'
import { JOB_STATUS, MILESTONE_LIST } from '@ProdWb/constants'
import { MILESTONE_LIST as milestone } from '@ProdWb/JobList/constants'
import { getJobRequestStatusTag } from '@ProdWb/logic'
import { useSelector } from 'react-redux'
import { isUserQCManager } from '@OpsWb/logic'

import { PartsWithFailedQty } from '../types'
import {
    ExceptionCreator,
    ExceptionFailureQuery,
    JobRequestsQuery,
    useJobPartsQuery,
    ExceptionFailureType,
} from '~/gql/graphql'
import Text from '~/core/Text'
import {
    DISCOVERY_LABLES,
    EXCEPTION_TYPES,
    RESPONSIBILITY_OPTIONS,
    FORM_RULES,
    EXCEPTION_FORM_FIELDS,
    INVENTORY_MOVE_STATE,
    ERP_SALE_ORDER_DELIVERY_STATE,
} from '../constants'
import style from '../styles.css'
import {
    disableResponsibility,
    shouldHideJobRequestSelect,
    isIFExceptionOnJob,
    shouldHideAffectedLines,
    useJobRequestParts,
    shouldRestrictRFISelectiion,
} from '../logic'
import Box from '~/core/Box'

import {
    ReceiptDOFormField,
    AffectedLinesFormField,
} from './ExceptionFormFields'

const { Option } = Select

type ExceptionDetailsPropsType = {
    form: WrappedFormUtils
    exception?: ExceptionFailureQuery['exceptionFailure']
    jobRequests: JobRequestsQuery['jobRequests']
    saleOrderId?: number
    setRestrictExceptionCreation: Dispatch<SetStateAction<boolean>>
    setRestrictExceptionOnRFI: Dispatch<SetStateAction<boolean>>
    jobId?: number
    jobRequestLoading?: boolean
    partsWithQty: PartsWithFailedQty[]
    setPartsWithQty: Dispatch<SetStateAction<PartsWithFailedQty[]>>
}

const ExceptionDetailsSection = (props: ExceptionDetailsPropsType) => {
    const {
        form,
        form: { getFieldDecorator },
        exception,
        jobRequests,
        setRestrictExceptionCreation,
        setRestrictExceptionOnRFI,
        jobId,
        jobRequestLoading,
        partsWithQty,
        setPartsWithQty,
    } = props

    const isQCManager = useSelector(isUserQCManager)

    const { JOB_REQUEST, TYPE, MILESTONE, ASSIGNED_ENTITY } =
        EXCEPTION_FORM_FIELDS

    const { data: jobRequestParts, loading: partsLoading } = useJobRequestParts(
        form.getFieldValue(JOB_REQUEST) || exception?.jobRequestId || null,
    )

    const [selectedJobRequestStatus, setSelectedJobRequestStatus] = useState<
        string | null
    >(null)

    const { data: jobParts } = useJobPartsQuery({
        variables: {
            jobId: (jobId || exception?.jobId) as number,
        },
        skip:
            (!jobId && !exception?.jobId) ||
            form.getFieldValue(TYPE) != ExceptionFailureType.RFI ||
            form.getFieldValue(JOB_REQUEST),
    })

    const getParts = () => {
        return form.getFieldValue(TYPE) === ExceptionFailureType.RFI &&
            !form.getFieldValue(JOB_REQUEST) &&
            jobRequests?.length === 0
            ? jobParts?.getJobParts
            : jobRequestParts
    }

    const shouldHideReceiptDO = () => {
        const existingJobRequestStatus =
            jobRequests?.find(
                item => item.id === form.getFieldValue(JOB_REQUEST),
            )?.status || null

        if (
            jobRequestLoading ||
            !form.getFieldValue(JOB_REQUEST) ||
            existingJobRequestStatus === JOB_STATUS.CANCELED ||
            shouldHideJobRequestSelect(form, jobRequests)
        ) {
            return true
        }
        return false
    }

    const getReceipts = () => {
        const selectedJobRequest = jobRequests?.find(
            request => request.id === form.getFieldValue(JOB_REQUEST),
        )

        if (form.getFieldValue(MILESTONE) === milestone.inspection) {
            const filteredQualityChecks = exception?.id
                ? selectedJobRequest?.qualityChecks?.filter(
                      receipt =>
                          receipt.id === exception.qcId ||
                          receipt.state !== INVENTORY_MOVE_STATE.CANCEL,
                  )
                : selectedJobRequest?.qualityChecks?.filter(
                      receipt => receipt.state !== INVENTORY_MOVE_STATE.CANCEL,
                  )

            return {
                receipts: filteredQualityChecks || null,
                isQC: true,
            }
        }

        const filteredReceipts = exception?.id
            ? selectedJobRequest?.receipts?.filter(
                  receipt =>
                      receipt.id === exception.receiptId ||
                      receipt.state !== INVENTORY_MOVE_STATE.CANCEL,
              )
            : selectedJobRequest?.receipts?.filter(
                  receipt => receipt.state !== INVENTORY_MOVE_STATE.CANCEL,
              )

        return { receipts: filteredReceipts || null, isQC: false }
    }

    const getDeliveries = () => {
        const selectedJobRequest = jobRequests?.find(
            request => request.id === form.getFieldValue(JOB_REQUEST),
        )

        const filteredDeliveries = exception?.id
            ? selectedJobRequest?.deliveryOrders?.filter(
                  delivery =>
                      delivery.id === exception.shipmentId ||
                      delivery.state !== ERP_SALE_ORDER_DELIVERY_STATE.CANCEL,
              )
            : selectedJobRequest?.deliveryOrders?.filter(
                  delivery =>
                      delivery.state !== ERP_SALE_ORDER_DELIVERY_STATE.CANCEL,
              )

        return filteredDeliveries || null
    }

    useEffect(() => {
        if (
            form.getFieldValue(TYPE) === ExceptionFailureType.RFI &&
            selectedJobRequestStatus === JOB_STATUS.CANCELED
        ) {
            setRestrictExceptionOnRFI(true)
        }
    })

    const onChangeResponsibility = (evt: RadioChangeEvent) => {
        if (
            !jobRequestLoading &&
            isIFExceptionOnJob(
                evt.target.value,
                jobRequests,
                setRestrictExceptionCreation,
            )
        ) {
            message.error(
                'Cannot create IF exception for a job with empty scheduling queue',
            )
            return
        }
        form.setFieldsValue({
            isFailure: false,
            milestoneType: null,
        })
    }

    return (
        <>
            <Text className={style.woLineTitleStyle}>Type</Text>
            <Form.Item
                className={`${style.woLineItemsStyle} ${style.formItemMargin}`}
            >
                {getFieldDecorator(TYPE, {
                    initialValue: exception
                        ? exception?.type
                        : ExceptionFailureType.OT,
                })(
                    <Radio.Group onChange={onChangeResponsibility}>
                        {EXCEPTION_TYPES.map((type, index) => (
                            <Row key={index}>
                                <Radio
                                    data-test-target={`${type.value}-filter-checkbox`}
                                    value={type.value}
                                    disabled={
                                        ((type.value ===
                                            ExceptionFailureType.IF ||
                                            type.value ===
                                                ExceptionFailureType.RFI) &&
                                            exception?.type ===
                                                ExceptionFailureType.OT &&
                                            exception.creator ===
                                                ExceptionCreator.auto) ||
                                        (type.value ===
                                            ExceptionFailureType.IF &&
                                            exception?.type ===
                                                ExceptionFailureType.RFI) ||
                                        (type.value ===
                                            ExceptionFailureType.RFI &&
                                            shouldRestrictRFISelectiion(
                                                form,
                                                exception,
                                            )) ||
                                        !!(
                                            exception?.correctiveActionRequestId &&
                                            type.value !== exception.type &&
                                            !isQCManager
                                        )
                                    }
                                >
                                    {type.label}
                                </Radio>
                            </Row>
                        ))}
                    </Radio.Group>,
                )}
            </Form.Item>
            {shouldRestrictRFISelectiion(form, exception) && (
                <Box direction="col" paddingBottom="16px">
                    <Text color="red">
                        Please de-escalate the failure in order to change the
                        exception type to RFI
                    </Text>
                </Box>
            )}
            {form.getFieldValue(TYPE) === ExceptionFailureType.OT && (
                <Form.Item
                    label={
                        <Text fontSize="16px" color="blackAlpha85">
                            Select milestone affected
                        </Text>
                    }
                    className={style.formItemMargin}
                >
                    {getFieldDecorator(MILESTONE, {
                        rules: FORM_RULES.milestoneType,
                        initialValue: exception?.milestoneType,
                    })(
                        <Select
                            className={style.selectLayoutStyle}
                            onChange={(value: string) => {
                                form.setFieldsValue({
                                    assignedEntity: undefined,
                                    selectedMilestone: value,
                                    receiptId: undefined,
                                })
                            }}
                            placeholder="Select milestone"
                            getPopupContainer={(triggerNode: HTMLElement) =>
                                triggerNode.parentNode as HTMLElement
                            }
                            disabled={
                                (exception?.type === ExceptionFailureType.OT &&
                                    exception?.creator ===
                                        ExceptionCreator.auto) ||
                                !!(
                                    exception?.correctiveActionRequestId &&
                                    !isQCManager
                                )
                            }
                        >
                            {MILESTONE_LIST.map((option, index: number) => (
                                <Option
                                    disabled={
                                        (option.value !==
                                            milestone.scheduling &&
                                            !jobRequests?.length) ||
                                        (exception?.correctiveActionRequestId &&
                                            isQCManager &&
                                            ((exception.milestoneType !==
                                                milestone.scheduling &&
                                                option.value ===
                                                    milestone.scheduling) ||
                                                (exception.milestoneType ===
                                                    milestone.scheduling &&
                                                    option.value !==
                                                        milestone.scheduling)))
                                    }
                                    value={option.value}
                                    key={index}
                                >
                                    {option.label}
                                </Option>
                            ))}
                        </Select>,
                    )}
                </Form.Item>
            )}
            {!shouldHideJobRequestSelect(form, jobRequests) && (
                <Form.Item
                    label={
                        <Text fontSize="16px" color="blackAlpha85">
                            Select job request
                        </Text>
                    }
                    className={style.formItemMargin}
                >
                    {getFieldDecorator(JOB_REQUEST, {
                        rules: FORM_RULES.selectedJobRequest,
                        initialValue: exception?.jobRequestId,
                    })(
                        <Select
                            className={style.selectLayoutStyle}
                            placeholder="Select job request"
                            onChange={value => {
                                form.setFieldsValue({
                                    selectedJobRequest: Number(value),
                                })

                                const jobRequestStatus =
                                    jobRequests?.find(
                                        item =>
                                            item.id ===
                                                form.getFieldValue(
                                                    JOB_REQUEST,
                                                ) || exception?.jobRequestId,
                                    )?.status || null

                                setSelectedJobRequestStatus(jobRequestStatus)
                            }}
                            getPopupContainer={(triggerNode: HTMLElement) =>
                                triggerNode.parentNode as HTMLElement
                            }
                            disabled={
                                (exception?.type === ExceptionFailureType.OT &&
                                    exception.creator ===
                                        ExceptionCreator.auto) ||
                                !!exception?.correctiveActionRequestId
                            }
                        >
                            {jobRequests?.map((option, index: number) => (
                                <Option value={option.id} key={index}>
                                    <Row>
                                        <Col span={21}>
                                            <Text>
                                                {option.mfgPartner.name}
                                            </Text>
                                        </Col>
                                        <Col span={3}>
                                            <StatusTag
                                                tag={getJobRequestStatusTag(
                                                    option.status,
                                                )}
                                            />
                                        </Col>
                                    </Row>
                                </Option>
                            ))}
                        </Select>,
                    )}
                </Form.Item>
            )}
            {!shouldHideReceiptDO() && !jobRequestLoading && (
                <ReceiptDOFormField
                    fieldDecorator={getFieldDecorator}
                    receipts={getReceipts()}
                    deliveryOrders={getDeliveries()}
                    exception={exception}
                />
            )}
            {form.getFieldValue(TYPE) !== ExceptionFailureType.RFI &&
                form.getFieldValue(MILESTONE) !==
                    milestone.customer_delivery && (
                    <>
                        <Text className={style.woLineTitleStyle}>
                            Responsibility
                        </Text>
                        <Form.Item
                            className={`${style.woLineItemsStyle} ${style.formItemMargin}`}
                        >
                            {getFieldDecorator(ASSIGNED_ENTITY, {
                                rules: FORM_RULES.assignedEntity,
                                initialValue: exception?.assignedEntity,
                            })(
                                <Radio.Group>
                                    {RESPONSIBILITY_OPTIONS.map(
                                        (type, index) => (
                                            <Row key={index}>
                                                <Radio
                                                    data-test-target={`${type.value}-filter-checkbox`}
                                                    value={type.value}
                                                    disabled={
                                                        disableResponsibility(
                                                            type,
                                                            form,
                                                        ) ||
                                                        (!!exception?.correctiveActionRequestId &&
                                                            type.value !==
                                                                exception.assignedEntity)
                                                    }
                                                >
                                                    {type.label}
                                                </Radio>
                                            </Row>
                                        ),
                                    )}
                                </Radio.Group>,
                            )}
                        </Form.Item>
                    </>
                )}
            {form.getFieldValue(TYPE) !== ExceptionFailureType.RFI && (
                <Form.Item
                    label={
                        <Text fontSize="16px" color="blackAlpha85">
                            Discovered by
                        </Text>
                    }
                    style={{ marginBottom: '16px' }}
                >
                    {getFieldDecorator(EXCEPTION_FORM_FIELDS.DISCOVERED_BY, {
                        rules: FORM_RULES.failureDiscoveredBy,
                        initialValue: exception?.failureDiscoveredBy,
                    })(
                        <Select
                            placeholder="Select who discovered the failure "
                            getPopupContainer={(triggerNode: HTMLElement) =>
                                triggerNode.parentNode as HTMLElement
                            }
                            className={style.otifFormSelect}
                            disabled={
                                !!(
                                    exception?.correctiveActionRequestId &&
                                    !isQCManager
                                )
                            }
                        >
                            {DISCOVERY_LABLES.map(value => (
                                <Option value={value.id} key={value.id}>
                                    <Text>{value.name}</Text>
                                </Option>
                            ))}
                        </Select>,
                    )}
                </Form.Item>
            )}
            {!shouldHideAffectedLines(form) && (
                <AffectedLinesFormField
                    fieldDecorator={getFieldDecorator}
                    setFieldsValue={form.setFieldsValue}
                    exception={exception}
                    parts={getParts()}
                    partsLoading={partsLoading}
                    partsWithQty={partsWithQty}
                    setPartsWithQty={setPartsWithQty}
                />
            )}
        </>
    )
}

export default ExceptionDetailsSection
